/**
 * STRIPE WEB CHECKOUT SERVICE
 * ============================
 * Handles Stripe checkout for web users.
 * 
 * Architecture:
 * 1. User clicks "Subscribe" on web
 * 2. We call /api/stripe/checkout with user_id
 * 3. Backend creates Stripe Checkout Session
 * 4. User is redirected to Stripe-hosted payment page
 * 5. After payment, Stripe notifies RevenueCat
 * 6. RevenueCat sends webhook to our backend
 * 7. User's Pro status is updated
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Create a Stripe checkout session and redirect to payment page
 * @param {string} userId - The user's app ID (REQUIRED for RevenueCat mapping)
 * @param {string} priceType - 'monthly', 'yearly', or 'lifetime'
 * @returns {Promise<{checkoutUrl: string, sessionId: string}>}
 */
export async function createCheckoutSession(userId, priceType = 'monthly') {
  if (!userId) {
    throw new Error('User ID is required for checkout');
  }

  const response = await fetch(`${API_URL}/api/stripe/checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_id: userId,
      price_type: priceType,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create checkout session');
  }

  const data = await response.json();
  return {
    checkoutUrl: data.checkout_url,
    sessionId: data.session_id,
  };
}

/**
 * Redirect to Stripe checkout
 * @param {string} userId - The user's app ID
 * @param {string} priceType - 'monthly', 'yearly', or 'lifetime'
 */
export async function redirectToCheckout(userId, priceType = 'monthly') {
  try {
    const { checkoutUrl } = await createCheckoutSession(userId, priceType);
    window.location.href = checkoutUrl;
  } catch (error) {
    console.error('[Stripe] Checkout error:', error);
    throw error;
  }
}

/**
 * Get the status of a checkout session
 * @param {string} sessionId - The Stripe session ID from URL params
 * @returns {Promise<Object>} Session status details
 */
export async function getSessionStatus(sessionId) {
  const response = await fetch(`${API_URL}/api/stripe/session/${sessionId}`);
  
  if (!response.ok) {
    throw new Error('Failed to get session status');
  }
  
  return response.json();
}

/**
 * Open customer portal for subscription management
 * @param {string} userId - The user's app ID
 * @returns {Promise<string>} Portal URL
 */
export async function openCustomerPortal(userId) {
  const response = await fetch(`${API_URL}/api/stripe/portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to open customer portal');
  }

  const data = await response.json();
  window.location.href = data.portal_url;
}

/**
 * Get Stripe configuration
 * @returns {Promise<Object>} Stripe config including prices
 */
export async function getStripeConfig() {
  const response = await fetch(`${API_URL}/api/stripe/config`);
  
  if (!response.ok) {
    throw new Error('Failed to get Stripe configuration');
  }
  
  return response.json();
}

/**
 * Check if Stripe is configured and available for web checkout
 * @returns {Promise<boolean>}
 */
export async function isStripeAvailable() {
  try {
    const response = await fetch(`${API_URL}/api/stripe/health`);
    const data = await response.json();
    return data.configured;
  } catch {
    return false;
  }
}

export default {
  createCheckoutSession,
  redirectToCheckout,
  getSessionStatus,
  openCustomerPortal,
  getStripeConfig,
  isStripeAvailable,
};
